import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Upload, X } from 'lucide-react'
import type { Classification } from '@/types'

interface PhotoEntry {
  file: File
  base64: string
  previewUrl: string
}

interface AddPersonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  onPersonCreated: () => void
}

const MAX_PHOTOS = 5
const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB

export function AddPersonDialog({
  open,
  onOpenChange,
  groupId,
  onPersonCreated,
}: AddPersonDialogProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [classificationId, setClassificationId] = useState('')
  const [notes, setNotes] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [photos, setPhotos] = useState<PhotoEntry[]>([])
  const [classifications, setClassifications] = useState<Classification[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open && groupId) {
      loadClassifications()
    }
  }, [open, groupId])

  const loadClassifications = async () => {
    try {
      const getClassificationsFn = httpsCallable(functions, 'getClassifications')
      const result = await getClassificationsFn({ groupId })
      const data = result.data as { success: boolean; classifications: Classification[] }
      if (data.success) {
        setClassifications(data.classifications)
        // Default to 'unknown' classification
        const unknown = data.classifications.find(c => c.name === 'unknown')
        if (unknown && !classificationId) {
          setClassificationId(unknown.id)
        }
      }
    } catch (err) {
      console.error('Failed to load classifications:', err)
    }
  }

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        // Strip the data URL prefix (data:image/...;base64,)
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files) return

    const newPhotos: PhotoEntry[] = []
    const errors: string[] = []

    for (let i = 0; i < files.length; i++) {
      if (photos.length + newPhotos.length >= MAX_PHOTOS) {
        errors.push(t('addPerson.maxPhotosError', { max: MAX_PHOTOS }))
        break
      }

      const file = files[i]

      if (!file.type.startsWith('image/')) {
        errors.push(t('addPerson.notImage', { name: file.name }))
        continue
      }

      if (file.size > MAX_FILE_SIZE) {
        errors.push(t('addPerson.tooLarge', { name: file.name }))
        continue
      }

      try {
        const base64 = await readFileAsBase64(file)
        const previewUrl = URL.createObjectURL(file)
        newPhotos.push({ file, base64, previewUrl })
      } catch (err) {
        errors.push(t('addPerson.failedToRead', { name: file.name }))
      }
    }

    if (errors.length > 0) {
      setError(errors.join('. '))
    }

    if (newPhotos.length > 0) {
      setPhotos(prev => [...prev, ...newPhotos])
    }
  }

  const removePhoto = (index: number) => {
    setPhotos(prev => {
      const removed = prev[index]
      URL.revokeObjectURL(removed.previewUrl)
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('addPerson.nameRequired'))
      return
    }

    if (photos.length === 0) {
      setError(t('addPerson.photoRequired'))
      return
    }

    if (!classificationId) {
      setError(t('addPerson.classificationRequired'))
      return
    }

    setSaving(true)
    setError(null)

    try {
      const createPersonFn = httpsCallable(functions, 'createPerson')
      await createPersonFn({
        groupId,
        name: name.trim(),
        classificationId,
        notes: notes.trim() || null,
        expiresAt: expiresAt || null,
        photos: photos.map(p => ({ base64: p.base64 })),
      })

      handleClose()
      onPersonCreated()
    } catch (err: any) {
      console.error('Failed to create person:', err)
      if (err.code === 'functions/permission-denied') {
        setError(t('addPerson.noPermission'))
      } else if (err.code === 'functions/invalid-argument') {
        setError(err.message || 'Invalid input')
      } else {
        setError(t('addPerson.failedToCreate'))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    // Revoke all preview URLs
    photos.forEach(p => URL.revokeObjectURL(p.previewUrl))
    setName('')
    setClassificationId('')
    setNotes('')
    setExpiresAt('')
    setPhotos([])
    setError(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('addPerson.title')}</DialogTitle>
          <DialogDescription>
            {t('addPerson.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Photos */}
          <div className="space-y-2">
            <Label>{t('addPerson.photos')} <span className="text-muted-foreground font-normal">({photos.length}/{MAX_PHOTOS})</span></Label>

            {/* Thumbnail preview strip */}
            {photos.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {photos.map((photo, index) => (
                  <div key={index} className="relative w-16 h-16 rounded-lg overflow-hidden border bg-paper-2 group">
                    <img
                      src={photo.previewUrl}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      className="absolute top-0 right-0 p-0.5 bg-black/60 rounded-bl-md text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removePhoto(index)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload area */}
            {photos.length < MAX_PHOTOS && (
              <div
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
                onDrop={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleFilesSelected(e.dataTransfer.files)
                }}
              >
                <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                <p className="text-sm text-muted-foreground">
                  {t('addPerson.clickOrDrag')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('addPerson.maxPhotos', { max: MAX_PHOTOS })}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
              </div>
            )}
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="person-name">{t('addPerson.name')}</Label>
            <Input
              id="person-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('addPerson.namePlaceholder')}
              autoFocus
            />
          </div>

          {/* Classification */}
          <div className="space-y-2">
            <Label htmlFor="person-classification">{t('addPerson.classification')}</Label>
            <Select
              value={classificationId}
              onValueChange={setClassificationId}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('addPerson.selectClassification')} />
              </SelectTrigger>
              <SelectContent>
                {classifications.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: c.color }}
                      />
                      {c.displayName}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="person-notes">{t('addPerson.notes')} <span className="text-muted-foreground font-normal">{t('addPerson.notesOptional')}</span></Label>
            <Textarea
              id="person-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('addPerson.notesPlaceholder')}
              rows={2}
            />
          </div>

          {/* Expiration Date */}
          <div className="space-y-2">
            <Label htmlFor="person-expires">{t('addPerson.expirationDate')} <span className="text-muted-foreground font-normal">{t('addPerson.expirationOptional')}</span></Label>
            <Input
              id="person-expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
            <p className="text-xs text-muted-foreground">
              {t('addPerson.expirationHint')}
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving} className="hidden sm:inline-flex">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim() || photos.length === 0 || !classificationId}
          >
            {saving ? t('common.creating') : t('addPerson.createPerson')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
